// /app/frontend/src/pages/MediaGallery.jsx
import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useDropzone } from 'react-dropzone';
import { 
  ArrowLeft, Upload, FolderPlus, Trash2, Edit2, Check, X, 
  Image, Video, HardDrive, Search, Grid, List, MoreVertical,
  FolderOpen, Handshake, Gift, ImageIcon, Plus, Download
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { toast } from 'sonner';
import api from '../lib/api';
import { getImageUrl } from '../lib/utils';

const ICON_MAP = {
  'handshake': Handshake,
  'image': ImageIcon,
  'gift': Gift,
  'folder': FolderOpen,
};

export default function MediaGallery() {
  const navigate = useNavigate();
  const [categories, setCategories] = useState([]);
  const [media, setMedia] = useState([]);
  const [storage, setStorage] = useState({ used: 0, limit: 500, percent_used: 0 });
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [viewMode, setViewMode] = useState('grid');
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [selectedMedia, setSelectedMedia] = useState([]);
  
  // Dialogs
  const [showNewCategory, setShowNewCategory] = useState(false);
  const [showEditCategory, setShowEditCategory] = useState(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(null);
  const [showMediaPreview, setShowMediaPreview] = useState(null);
  
  // New category form
  const [newCategoryName, setNewCategoryName] = useState('');
  const [newCategoryIcon, setNewCategoryIcon] = useState('folder');

  useEffect(() => {
    loadData();
  }, []);

  useEffect(() => {
    if (!loading) {
      loadMedia();
    }
  }, [selectedCategory, loading]);

  const loadData = async () => {
    try {
      setLoading(true);
      const [catRes, storageRes] = await Promise.all([
        api.get('/api/media/categories'),
        api.get('/api/media/storage')
      ]);
      setCategories(catRes.data);
      setStorage(storageRes.data);
    } catch (error) {
      console.error('Error loading data:', error);
      toast.error('Erreur de chargement des données');
    } finally {
      setLoading(false);
    }
  };

  const loadMedia = async () => {
    try {
      const params = selectedCategory ? `?category_id=${selectedCategory}` : '';
      const res = await api.get(`/api/media${params}`);
      setMedia(res.data);
    } catch (error) {
      console.error('Error loading media:', error);
      // Don't show toast for media loading errors, just log
    }
  };

  const onDrop = useCallback(async (acceptedFiles) => {
    if (acceptedFiles.length === 0) return;
    
    setUploading(true);
    const formData = new FormData();
    acceptedFiles.forEach(file => {
      formData.append('files', file);
    });
    if (selectedCategory) {
      formData.append('category_id', selectedCategory);
    }

    try {
      const res = await api.post('/api/media/upload-multiple', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      
      if (res.data.success_count > 0) {
        toast.success(`${res.data.success_count} fichier(s) uploadé(s)`);
        loadMedia();
        loadData(); // Refresh storage info
      }
      if (res.data.errors?.length > 0) {
        res.data.errors.forEach(err => toast.error(`${err.filename}: ${err.error}`));
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur d\'upload');
    } finally {
      setUploading(false);
    }
  }, [selectedCategory]);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      'image/*': ['.jpg', '.jpeg', '.png', '.gif', '.webp', '.svg'],
      'video/*': ['.mp4', '.mov', '.webm']
    },
    maxSize: 50 * 1024 * 1024
  });

  const createCategory = async () => {
    if (!newCategoryName.trim()) return;
    
    try {
      const res = await api.post('/api/media/categories', {
        name: newCategoryName,
        icon: newCategoryIcon
      });
      setCategories([...categories, res.data]);
      setShowNewCategory(false);
      setNewCategoryName('');
      toast.success('Catégorie créée');
    } catch (error) {
      toast.error('Erreur de création');
    }
  };

  const deleteCategory = async (categoryId) => {
    try {
      await api.delete(`/api/media/categories/${categoryId}`);
      setCategories(categories.filter(c => c.id !== categoryId));
      if (selectedCategory === categoryId) setSelectedCategory(null);
      setShowDeleteConfirm(null);
      toast.success('Catégorie supprimée');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur de suppression');
    }
  };

  const deleteMedia = async (mediaId) => {
    try {
      await api.delete(`/api/media/${mediaId}`);
      setMedia(media.filter(m => m.id !== mediaId));
      loadData(); // Refresh storage
      toast.success('Média supprimé');
    } catch (error) {
      toast.error('Erreur de suppression');
    }
  };

  const deleteSelectedMedia = async () => {
    if (selectedMedia.length === 0) return;
    
    try {
      await api.delete('/api/media', { data: selectedMedia });
      setMedia(media.filter(m => !selectedMedia.includes(m.id)));
      setSelectedMedia([]);
      loadData();
      toast.success(`${selectedMedia.length} média(s) supprimé(s)`);
    } catch (error) {
      toast.error('Erreur de suppression');
    }
  };

  const toggleMediaSelection = (mediaId) => {
    setSelectedMedia(prev => 
      prev.includes(mediaId) 
        ? prev.filter(id => id !== mediaId)
        : [...prev, mediaId]
    );
  };

  const filteredMedia = media.filter(m => 
    m.name?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const formatSize = (bytes) => {
    if (bytes < 1024) return `${bytes} o`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} Ko`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} Mo`;
  };

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
      {/* Header */}
      <div className="bg-white dark:bg-gray-800 border-b dark:border-gray-700 px-6 py-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button variant="ghost" size="icon" onClick={() => navigate('/dashboard')}>
              <ArrowLeft className="h-5 w-5" />
            </Button>
            <div>
              <h1 className="text-2xl font-bold dark:text-white">Galerie Média</h1>
              <p className="text-sm text-gray-500 dark:text-gray-400">
                Gérez vos images et vidéos
              </p>
            </div>
          </div>
          
          {/* Storage indicator */}
          <div className="flex items-center gap-4">
            <div className="text-right">
              <div className="flex items-center gap-2 text-sm text-gray-600 dark:text-gray-300">
                <HardDrive className="h-4 w-4" />
                <span>{storage.used_mb} Mo / {storage.limit_mb} Mo</span>
                {storage.is_premium && (
                  <span className="px-2 py-0.5 bg-yellow-100 text-yellow-800 text-xs rounded-full">Premium</span>
                )}
              </div>
              <div className="w-48 h-2 bg-gray-200 dark:bg-gray-700 rounded-full mt-1">
                <div 
                  className={`h-full rounded-full transition-all ${
                    storage.percent_used > 90 ? 'bg-red-500' : 
                    storage.percent_used > 70 ? 'bg-yellow-500' : 'bg-green-500'
                  }`}
                  style={{ width: `${Math.min(storage.percent_used, 100)}%` }}
                />
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="flex h-[calc(100vh-80px)]">
        {/* Sidebar - Categories */}
        <div className="w-64 bg-white dark:bg-gray-800 border-r dark:border-gray-700 p-4 overflow-y-auto">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-semibold dark:text-white">Catégories</h3>
            <Button 
              variant="ghost" 
              size="icon"
              onClick={() => setShowNewCategory(true)}
              data-testid="add-category-btn"
            >
              <FolderPlus className="h-4 w-4" />
            </Button>
          </div>

          {/* All media */}
          <button
            onClick={() => setSelectedCategory(null)}
            className={`w-full flex items-center gap-3 px-3 py-2 rounded-lg mb-2 transition-colors ${
              selectedCategory === null 
                ? 'bg-blue-100 dark:bg-blue-900 text-blue-700 dark:text-blue-300' 
                : 'hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-700 dark:text-gray-300'
            }`}
            data-testid="all-media-btn"
          >
            <Grid className="h-4 w-4" />
            <span>Tous les médias</span>
            <span className="ml-auto text-xs bg-gray-200 dark:bg-gray-600 px-2 py-0.5 rounded-full">
              {media.length}
            </span>
          </button>

          {/* Category list */}
          {categories.map(cat => {
            const IconComponent = ICON_MAP[cat.icon] || FolderOpen;
            return (
              <div key={cat.id} className="group relative">
                <button
                  onClick={() => setSelectedCategory(cat.id)}
                  className={`w-full flex items-center gap-3 px-3 py-2 rounded-lg mb-1 transition-colors ${
                    selectedCategory === cat.id 
                      ? 'bg-blue-100 dark:bg-blue-900 text-blue-700 dark:text-blue-300' 
                      : 'hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-700 dark:text-gray-300'
                  }`}
                  data-testid={`category-${cat.id}`}
                >
                  <IconComponent className="h-4 w-4" />
                  <span className="truncate flex-1 text-left">{cat.name}</span>
                  <span className="text-xs bg-gray-200 dark:bg-gray-600 px-2 py-0.5 rounded-full">
                    {cat.media_count || 0}
                  </span>
                </button>
                
                {!cat.is_default && (
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      setShowDeleteConfirm(cat);
                    }}
                    className="absolute right-1 top-1/2 -translate-y-1/2 opacity-0 group-hover:opacity-100 p-1 hover:bg-red-100 dark:hover:bg-red-900 rounded"
                  >
                    <Trash2 className="h-3 w-3 text-red-500" />
                  </button>
                )}
              </div>
            );
          })}
        </div>

        {/* Main content */}
        <div className="flex-1 p-6 overflow-y-auto">
          {/* Toolbar */}
          <div className="flex items-center gap-4 mb-6">
            <div className="relative flex-1 max-w-md">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
              <Input
                placeholder="Rechercher..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10"
                data-testid="search-media"
              />
            </div>
            
            <div className="flex items-center gap-2">
              <Button
                variant={viewMode === 'grid' ? 'default' : 'outline'}
                size="icon"
                onClick={() => setViewMode('grid')}
              >
                <Grid className="h-4 w-4" />
              </Button>
              <Button
                variant={viewMode === 'list' ? 'default' : 'outline'}
                size="icon"
                onClick={() => setViewMode('list')}
              >
                <List className="h-4 w-4" />
              </Button>
            </div>

            {selectedMedia.length > 0 && (
              <Button 
                variant="destructive" 
                onClick={deleteSelectedMedia}
                data-testid="delete-selected-btn"
              >
                <Trash2 className="h-4 w-4 mr-2" />
                Supprimer ({selectedMedia.length})
              </Button>
            )}
          </div>

          {/* Drop zone */}
          <div
            {...getRootProps()}
            className={`border-2 border-dashed rounded-xl p-8 mb-6 text-center transition-colors cursor-pointer ${
              isDragActive 
                ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20' 
                : 'border-gray-300 dark:border-gray-600 hover:border-blue-400'
            }`}
            data-testid="dropzone"
          >
            <input {...getInputProps()} />
            {uploading ? (
              <div className="flex items-center justify-center gap-3">
                <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-500" />
                <span className="text-gray-600 dark:text-gray-300">Upload en cours...</span>
              </div>
            ) : (
              <>
                <Upload className="h-10 w-10 mx-auto mb-3 text-gray-400" />
                <p className="text-gray-600 dark:text-gray-300 mb-1">
                  {isDragActive ? 'Déposez les fichiers ici' : 'Glissez-déposez vos fichiers ou cliquez pour sélectionner'}
                </p>
                <p className="text-sm text-gray-500">
                  Images (JPG, PNG, GIF, WebP) • Vidéos (MP4, 5s max) • Auto-compression WebP
                </p>
              </>
            )}
          </div>

          {/* Media grid/list */}
          {loading ? (
            <div className="flex justify-center py-12">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500" />
            </div>
          ) : filteredMedia.length === 0 ? (
            <div className="text-center py-12 text-gray-500">
              <Image className="h-12 w-12 mx-auto mb-3 opacity-50" />
              <p>Aucun média dans cette catégorie</p>
            </div>
          ) : viewMode === 'grid' ? (
            <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4">
              {filteredMedia.map(item => (
                <div 
                  key={item.id}
                  className={`group relative aspect-square rounded-lg overflow-hidden bg-gray-100 dark:bg-gray-800 cursor-pointer border-2 transition-all ${
                    selectedMedia.includes(item.id) 
                      ? 'border-blue-500 ring-2 ring-blue-200' 
                      : 'border-transparent hover:border-gray-300'
                  }`}
                  onClick={() => setShowMediaPreview(item)}
                  data-testid={`media-${item.id}`}
                >
                  {item.media_type === 'video' ? (
                    <video 
                      src={getImageUrl(item.url)} 
                      className="w-full h-full object-cover"
                      muted
                    />
                  ) : (
                    <img 
                      src={getImageUrl(item.url)} 
                      alt={item.name}
                      className="w-full h-full object-cover"
                    />
                  )}
                  
                  {/* Overlay */}
                  <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                    <div className="flex gap-2">
                      <button 
                        onClick={(e) => { e.stopPropagation(); toggleMediaSelection(item.id); }}
                        className={`p-2 rounded-full ${selectedMedia.includes(item.id) ? 'bg-blue-500' : 'bg-white/20 hover:bg-white/40'}`}
                      >
                        <Check className="h-4 w-4 text-white" />
                      </button>
                      <button 
                        onClick={(e) => { e.stopPropagation(); deleteMedia(item.id); }}
                        className="p-2 bg-red-500/80 hover:bg-red-500 rounded-full"
                      >
                        <Trash2 className="h-4 w-4 text-white" />
                      </button>
                    </div>
                  </div>
                  
                  {/* Type badge */}
                  <div className="absolute top-2 left-2">
                    {item.media_type === 'video' ? (
                      <Video className="h-4 w-4 text-white drop-shadow" />
                    ) : (
                      <Image className="h-4 w-4 text-white drop-shadow" />
                    )}
                  </div>
                  
                  {/* Size badge */}
                  <div className="absolute bottom-2 right-2 text-xs text-white bg-black/50 px-2 py-0.5 rounded">
                    {formatSize(item.file_size)}
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="space-y-2">
              {filteredMedia.map(item => (
                <div 
                  key={item.id}
                  className={`flex items-center gap-4 p-3 rounded-lg bg-white dark:bg-gray-800 border transition-all cursor-pointer ${
                    selectedMedia.includes(item.id) 
                      ? 'border-blue-500' 
                      : 'border-gray-200 dark:border-gray-700 hover:border-gray-300'
                  }`}
                  onClick={() => setShowMediaPreview(item)}
                  data-testid={`media-list-${item.id}`}
                >
                  <div className="w-16 h-16 rounded overflow-hidden bg-gray-100 dark:bg-gray-700 flex-shrink-0">
                    {item.media_type === 'video' ? (
                      <video src={getImageUrl(item.url)} className="w-full h-full object-cover" muted />
                    ) : (
                      <img src={getImageUrl(item.url)} alt={item.name} className="w-full h-full object-cover" />
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="font-medium dark:text-white truncate">{item.name}</p>
                    <p className="text-sm text-gray-500">
                      {item.media_type === 'video' ? 'Vidéo' : 'Image'} • {formatSize(item.file_size)}
                      {item.width && ` • ${item.width}x${item.height}`}
                    </p>
                  </div>
                  <div className="flex items-center gap-2">
                    <Button 
                      variant="ghost" 
                      size="icon"
                      onClick={(e) => { e.stopPropagation(); toggleMediaSelection(item.id); }}
                    >
                      <Check className={`h-4 w-4 ${selectedMedia.includes(item.id) ? 'text-blue-500' : ''}`} />
                    </Button>
                    <Button 
                      variant="ghost" 
                      size="icon"
                      onClick={(e) => { e.stopPropagation(); deleteMedia(item.id); }}
                    >
                      <Trash2 className="h-4 w-4 text-red-500" />
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* New Category Dialog */}
      <Dialog open={showNewCategory} onOpenChange={setShowNewCategory}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Nouvelle catégorie</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <Input
              placeholder="Nom de la catégorie"
              value={newCategoryName}
              onChange={(e) => setNewCategoryName(e.target.value)}
              data-testid="new-category-name"
            />
            <div>
              <label className="text-sm font-medium mb-2 block">Icône</label>
              <div className="flex gap-2">
                {Object.entries(ICON_MAP).map(([key, Icon]) => (
                  <button
                    key={key}
                    onClick={() => setNewCategoryIcon(key)}
                    className={`p-3 rounded-lg border-2 transition-colors ${
                      newCategoryIcon === key 
                        ? 'border-blue-500 bg-blue-50 dark:bg-blue-900' 
                        : 'border-gray-200 dark:border-gray-600 hover:border-gray-300'
                    }`}
                  >
                    <Icon className="h-5 w-5" />
                  </button>
                ))}
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowNewCategory(false)}>Annuler</Button>
            <Button onClick={createCategory} data-testid="create-category-btn">Créer</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Category Confirm */}
      <Dialog open={!!showDeleteConfirm} onOpenChange={() => setShowDeleteConfirm(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Supprimer la catégorie</DialogTitle>
          </DialogHeader>
          <p className="text-gray-600 dark:text-gray-300">
            Êtes-vous sûr de vouloir supprimer la catégorie "{showDeleteConfirm?.name}" ?
            Les médias seront déplacés vers "Non classé".
          </p>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDeleteConfirm(null)}>Annuler</Button>
            <Button variant="destructive" onClick={() => deleteCategory(showDeleteConfirm?.id)}>
              Supprimer
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Media Preview Dialog */}
      <Dialog open={!!showMediaPreview} onOpenChange={() => setShowMediaPreview(null)}>
        <DialogContent className="max-w-3xl">
          <DialogHeader>
            <DialogTitle>{showMediaPreview?.name}</DialogTitle>
          </DialogHeader>
          <div className="flex justify-center py-4">
            {showMediaPreview?.media_type === 'video' ? (
              <video 
                src={getImageUrl(showMediaPreview?.url)} 
                controls 
                autoPlay 
                muted 
                loop
                className="max-h-[60vh] rounded-lg"
              />
            ) : (
              <img 
                src={getImageUrl(showMediaPreview?.url)} 
                alt={showMediaPreview?.name}
                className="max-h-[60vh] rounded-lg"
              />
            )}
          </div>
          <div className="flex justify-between items-center text-sm text-gray-500">
            <span>
              {showMediaPreview?.width && `${showMediaPreview.width}x${showMediaPreview.height} • `}
              {formatSize(showMediaPreview?.file_size || 0)}
              {showMediaPreview?.original_size && ` (original: ${formatSize(showMediaPreview.original_size)})`}
            </span>
            <Button
              variant="outline"
              size="sm"
              onClick={() => {
                const url = getImageUrl(showMediaPreview?.url);
                window.open(url, '_blank');
              }}
            >
              <Download className="h-4 w-4 mr-2" />
              Télécharger
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
